import { useId, useMemo } from 'react'
import type { MascotConfig } from '../mascot-catalog'
import type { GameSession } from './mascot-game-types'
import { FrisbeePet } from './FrisbeePet'
import { FrisbeePark } from './FrisbeePark'
import { FrisbeeDisc } from './FrisbeeDisc'
import { victoryPose } from './frisbee-victory'
import { FrisbeeThrower } from './FrisbeeThrower'
import { THROW_RELEASE, throwPose } from './frisbee-throw'
import { DEFAULT_AVATAR, type AvatarConfig } from '../../donorAvatar/avatar-catalog'
import { avatarWithCustomBases } from '../../donorAvatar/avatar-custom'
import {
  jumpHeight,
  COIN_HEIGHT,
  MAX_PICKUP_COINS,
  obstacleX,
  OBSTACLES,
  RUN_DURATION,
  runDistance,
  type RunState,
} from './frisbee-engine'
import styles from './FrisbeeGame.module.css'

export function FrisbeeScene({
  run,
  session,
  mascot,
  avatar,
  moving,
  victoryAt,
  reducedVictory = false,
}: {
  run: RunState
  session: GameSession
  mascot: MascotConfig
  avatar?: AvatarConfig | null
  moving: boolean
  victoryAt?: number
  reducedVictory?: boolean
}) {
  const id = useId().replace(/:/g, '')
  const throwConfig = useMemo(
    () => avatarWithCustomBases(avatar ?? DEFAULT_AVATAR),
    [avatar]
  )
  const distance = runDistance(run.at)
  const victory = victoryAt === undefined ? null : victoryPose(victoryAt, reducedVictory)
  const height = victory?.height ?? jumpHeight(run.at, run.jumpAt)
  const petX = victory?.x ?? 130
  const won = run.at === RUN_DURATION
  const stage = Math.min(3, Math.floor(run.at / 150000))
  const launch = throwPose(Math.min(run.at, THROW_RELEASE), throwConfig)
  const flight = Math.max(
    0,
    Math.min(1, (run.at - THROW_RELEASE) / (1500 - THROW_RELEASE))
  )
  const discX =
    run.at < 1500
      ? launch.handX + flight * (535 - launch.handX)
      : 535 - Math.max(0, (run.at - 598500) / 1500) * 275
  const approach = Math.max(0, (run.at - 598500) / 1500)
  const discY =
    run.at < 1500
      ? launch.handY +
        flight * (130 + Math.sin(1500 / 360) * 9 - launch.handY) -
        Math.sin(flight * Math.PI) * 24
      : 130 + Math.sin(run.at / 360) * 9 * (1 - approach)
  return (
    <svg
      className={styles.landscape}
      viewBox="0 0 640 320"
      role="img"
      aria-label={
        victory?.caught
          ? `${mascot.name || 'Seu mascote'} pegou o frisbee!`
          : `${mascot.name || 'Seu mascote'} correndo atrás do frisbee ${mascot.backdrop === 'farm' ? 'na fazendinha' : mascot.species === 'jaguar' ? 'na selva' : 'no parque'}`
      }
      data-moving={moving}
      data-airborne={height > 0}
    >
      <defs>
        <linearGradient id={`${id}-coin`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#fff6a6" />
          <stop offset=".45" stopColor="#ffd451" />
          <stop offset="1" stopColor="#d78a18" />
        </linearGradient>
      </defs>
      <FrisbeePark
        jungle={mascot.species === 'jaguar' && mascot.backdrop !== 'farm'}
        farm={mascot.backdrop === 'farm'}
        id={id}
        at={run.at}
        distance={distance}
        stage={stage}
        reducedMotion={reducedVictory}
      />
      {run.at < 2400 && <FrisbeeThrower avatar={avatar} at={run.at} />}
      {!victory?.caught && (
        <g
          transform={`translate(${victory?.discX ?? discX} ${victory?.discY ?? discY}) rotate(-9)`}
        >
          {!won && run.at >= THROW_RELEASE && (
            <path
              d="M-33-3h-20m16 7h-24"
              stroke="#fff7db"
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}
          <FrisbeeDisc />
        </g>
      )}
      {(session.encounters ?? [])
        .slice(run.cleared, run.cleared + 4)
        .map((at, offset) => {
          const index = run.cleared + offset
          const kind = session.values[index]
          const shape = OBSTACLES[kind]
          const x = obstacleX(run.at, at)
          if (x > 710 || x < -70) return null
          return (
            <g key={index} transform={`translate(${x - shape.width / 2} 266)`}>
              <ellipse
                cx={shape.width / 2}
                cy="2"
                rx={shape.width * 0.65}
                ry="4"
                fill="#4b5e45"
                opacity=".2"
              />
              {kind === 0 ? (
                <>
                  <path
                    d="M0 0 3-20 13-32 25-26 30-9 30 0Z"
                    fill="#4d5b70"
                    stroke="#26364c"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path d="m3-20 10-12 12 6-12 6Z" fill="#aabbd1" />
                  <path d="m13-20-2 14" stroke="#27384e" strokeWidth="2" />
                  <path
                    d="m5-21 8-8 10 5"
                    fill="none"
                    stroke="#e1eaf3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </>
              ) : kind === 1 ? (
                <>
                  <rect y="-44" width="44" height="44" rx="8" fill="#aa7651" />
                  <path
                    d="M7-37h28M9-29v23m10-21v15m11-18v24"
                    stroke="#d3a578"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <ellipse cx="22" cy="-39" rx="18" ry="5" fill="#e4be8c" />
                  <ellipse cx="22" cy="-39" rx="10" ry="2" fill="none" stroke="#aa7651" />
                </>
              ) : kind === 2 ? (
                <>
                  <path d="m0-21 7 21h46l7-21Z" fill="#bf7857" />
                  <path d="M5-22q-4-15 12-11 8-8 15 2 15-10 21 9" fill="#5e915c" />
                  <path d="M0-21h60" stroke="#e4a47c" strokeWidth="5" />
                  <circle cx="17" cy="-31" r="4" fill="#ffe497" />
                  <circle cx="42" cy="-29" r="4" fill="#f4c4b5" />
                </>
              ) : kind === 3 ? (
                <>
                  <path
                    d="M5-5 12-46q4-4 8 0L27-5Z"
                    fill="#ec7134"
                    stroke="#84371f"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path d="m10-33 12 0 2 9H8Zm-4 21h20l-2-8H8Z" fill="#fff1d0" />
                  <rect x="0" y="-6" width="32" height="6" rx="2" fill="#613f35" />
                  <path
                    d="m14-43-4 26"
                    stroke="#ffbb73"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </>
              ) : kind === 4 ? (
                <>
                  <rect
                    x="1"
                    y="-45"
                    width="46"
                    height="44"
                    rx="3"
                    fill="#b67b43"
                    stroke="#613d28"
                    strokeWidth="2"
                  />
                  <path
                    d="M5-31h38M5-16h38M17-42v10m17 4v11M18-13v9"
                    stroke="#80522e"
                    strokeWidth="2"
                  />
                  <path d="M5-41 43-5m0-36L5-5" stroke="#edc18b" strokeWidth="6" />
                  <path d="M4-43h40M4-3h40" stroke="#f9d8a0" strokeWidth="3" />
                  <g fill="#674730">
                    <circle cx="7" cy="-39" r="1.5" />
                    <circle cx="41" cy="-39" r="1.5" />
                    <circle cx="7" cy="-7" r="1.5" />
                    <circle cx="41" cy="-7" r="1.5" />
                  </g>
                </>
              ) : kind === 5 ? (
                <>
                  <circle
                    cx="19"
                    cy="-19"
                    r="18"
                    fill="#344657"
                    stroke="#1d2c39"
                    strokeWidth="2"
                  />
                  <circle
                    cx="19"
                    cy="-19"
                    r="12"
                    fill="none"
                    stroke="#73899a"
                    strokeWidth="2"
                  />
                  <circle
                    cx="19"
                    cy="-19"
                    r="7"
                    fill="#e8d7ad"
                    stroke="#172e3a"
                    strokeWidth="2"
                  />
                  <path
                    d="m9-30 4 3m13-3-3 3M5-20l4 1m20 0 4-1M10-8l3-4m12 4-2-4"
                    stroke="#8ba0aa"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </>
              ) : (
                <>
                  <path d="M10-37V-2m48-35v35" stroke="#3b5757" strokeWidth="6" />
                  <path
                    d="M4-2h12m36 0h12"
                    stroke="#263e40"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <rect
                    x="1"
                    y="-41"
                    width="66"
                    height="16"
                    rx="3"
                    fill="#fff2c4"
                    stroke="#80512c"
                    strokeWidth="2"
                  />
                  <path
                    d="m8-40 9 14m9-14 9 14m9-14 9 14"
                    stroke="#d77137"
                    strokeWidth="8"
                  />
                  <path d="M5-39h58" stroke="#fff9e8" strokeWidth="2" />
                </>
              )}
            </g>
          )
        })}
      {(session.coins ?? []).slice(0, MAX_PICKUP_COINS).map((at, index) => {
        if (index < run.coinIndex) return null
        const x = obstacleX(run.at, at)
        if (x > 680 || x < -40) return null
        const bob = Math.sin(run.at / 220 + index) * 3
        const shine = 0.45 + Math.sin(run.at / 180 + index) * 0.25
        return (
          <g key={index} transform={`translate(${x} ${266 - COIN_HEIGHT + bob})`}>
            <circle r="24" fill="#fff6b4" opacity={shine * 0.5} />
            <circle r="16" fill={`url(#${id}-coin)`} stroke="#845315" strokeWidth="2" />
            <circle r="11.5" fill="none" stroke="#fff1a0" strokeWidth="1.5" />
            <path
              d="M-10-8q6-8 15-3"
              fill="none"
              stroke="#fffbe2"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <g fill="#965d17">
              <ellipse cy="4" rx="5" ry="4" />
              <ellipse cx="-6" cy="-2" rx="2" ry="2.8" transform="rotate(-20 -6 -2)" />
              <ellipse cx="0" cy="-5" rx="2" ry="2.8" />
              <ellipse cx="6" cy="-2" rx="2" ry="2.8" transform="rotate(20 6 -2)" />
            </g>
            <path
              d="m21-24 2 6 6 2-6 2-2 6-2-6-6-2 6-2Z"
              fill="#fffbe0"
              opacity={shine + 0.25}
            />
          </g>
        )
      })}
      {run.at - run.lastCoinAt < 650 && (
        <g
          transform={`translate(130 ${266 - COIN_HEIGHT - (run.at - run.lastCoinAt) * 0.055})`}
          opacity={1 - (run.at - run.lastCoinAt) / 650}
          aria-hidden="true"
        >
          <circle
            r={18 + (run.at - run.lastCoinAt) * 0.035}
            fill="none"
            stroke="#fff3ad"
            strokeWidth="2"
          />
          <text
            textAnchor="middle"
            y="-10"
            fill="#fff9d5"
            stroke="#815014"
            strokeWidth="3"
            paintOrder="stroke"
            fontSize="20"
            fontWeight="800"
          >
            +1
          </text>
          <path
            d="m-25-5 3-7m43 0 4 7m-27-18v-6"
            stroke="#fff3ad"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>
      )}
      <ellipse
        cx={petX}
        cy="270"
        rx={30 - height / 7}
        ry="4"
        fill="#435f50"
        opacity={(0.2 - height / 900) * (victory?.opacity ?? 1)}
      />
      <g transform={`translate(${petX} ${266 - height})`} opacity={victory?.opacity ?? 1}>
        <FrisbeePet
          config={mascot}
          at={victory?.petAt ?? run.at}
          jumpAt={victory?.jumpAt ?? run.jumpAt}
          carryingFrisbee={victory?.caught}
        />
      </g>
      {moving && height === 0 && !victory && (
        <g className={styles.dust} fill="#f7edce">
          {[0, 1, 2].map((index) => {
            const life = (run.at / 430 + index / 3) % 1
            return (
              <ellipse
                key={index}
                cx={103 - life * 32}
                cy={265 - life * 9}
                rx={1 + life * 4}
                ry={1 + life * 2}
                opacity={(1 - life) * 0.45}
              />
            )
          })}
        </g>
      )}
      {victory?.caught && !victory.gone && (
        <g
          fill="#fff5b8"
          transform={`translate(${petX} ${266 - height})`}
          opacity={victory.opacity}
        >
          <path d="m65-75 3 8 9 2-8 4-2 9-4-8-8-2 8-4Z" />
          <path d="m-25-88 2 6 7 2-7 3-2 6-2-6-7-3 7-2Z" />
        </g>
      )}
    </svg>
  )
}
